import { useCallback, useState } from "react";
import { Navigate, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { AuthRequired } from "@/components/auth/AuthRequired";
import { SubPage } from "@/components/profile/SettingsKit";
import { Button, Skeleton } from "@/components/ui";
import { ConversionDialog } from "@/components/wallet/ConversionDialog";
import { ConversionHistoryCard } from "@/components/wallet/ConversionHistoryCard";
import { WalletBalancesPanel, type WalletDirection } from "@/components/wallet/WalletBalancesPanel";
import { keys } from "@/lib/queries";
import { buildHead } from "@/lib/seo";
import { useSessionAccount } from "@/lib/useSessionAccount";
import { useTradingMode, walletPageEnabled } from "@/mode";

/**
 * What the user came here to do. Two of them are rail directions the
 * page merely EMPHASISES; `convert` is different — it is a navigation
 * that arrived, so the page opens the conversion flow and, when it
 * closes, hands the user back where they pressed the button (SM-508).
 */
type WalletIntent = WalletDirection | "convert";

// /wallet — the three-wallet surface (SM-445).
//
// Behind VITE_FF_WALLET_MULTI: the client rolls out independently of
// Backoffice and MM, and per the SM-409 inventory a UI flag may only be
// on once this app's own error handling is deployed behind it. With the
// flag off the route 404s, which is the same thing a pre-epic build
// did, so nothing regresses.
//
// What appears here follows the active trading mode, not the API's
// answer: CASH shows GOLD, GAME shows RED and GREEN. GOLD is absent
// from a GAME page except inside a conversion — see WalletBalancesPanel.
//
// SM-487: the page is GAME-only. In CASH it REDIRECTS to /portfolio
// rather than 404ing — see walletPageEnabled for why the page does not
// belong in CASH at all. A redirect and not a NotFound because CASH
// reaches here by three routes that are all ordinary use, not error:
// a bookmark made in GAME, the back button after a mode switch, and the
// switch itself while the page is open. Each of those is a user who
// wants their money, and Portfolio is where it is.
//
// SM-764: a broker-funded session (bootstrap balance_mode other than
// SYSTEM_BROKER) keeps the page — its balances are its own — but not the
// conversion: its broker has no conversion policy (BROKER_FUNDED) or the
// mode forbids it outright (THIRD_PARTY_CREDIT), and the ledger refuses
// it server-side regardless. The CTA, the history and the dialog are not
// drawn, and `?action=convert` opens nothing.

export const Route = createFileRoute("/_app/wallet/")({
  head: () =>
    buildHead({
      path: "/wallet",
      title: "Wallet",
      description: "Your balances and conversions.",
      noindex: true,
    }),
  // `?action=deposit|withdraw|convert` — what the user came here to do.
  //
  // The two directions are a HINT only (SM-487, from the Portfolio
  // hero): they emphasise a direction on each wallet card and never
  // decide whether it is permitted, which stays the ledger's answer
  // alone. `convert` (SM-508) is more than a hint — it opens the
  // conversion flow — but it still asserts nothing about whether any
  // route is open. Anything else is dropped rather than rejected; a bad
  // hint is not worth a 404.
  validateSearch: (s: Record<string, unknown>): { action?: WalletIntent } => ({
    action:
      s.action === "deposit" || s.action === "withdraw" || s.action === "convert" ? s.action : undefined,
  }),
  component: () => (
    <AuthRequired>
      <WalletPage />
    </AuthRequired>
  ),
});

function WalletPage() {
  const { t } = useTranslation("wallet");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { mode, resolving } = useTradingMode();
  const { action } = Route.useSearch();
  const { conversionAllowed } = useSessionAccount();
  // Arriving with `?action=convert` opens the flow immediately — the
  // Portfolio's Convert button navigates here rather than growing a
  // second conversion of its own.
  const [convertOpen, setConvertOpen] = useState(action === "convert" && conversionAllowed);

  // Closing the dialog after a navigated-in conversion returns the user
  // to where they pressed Convert, with the balances re-read.
  //
  // The refresh runs on CANCEL too. Nothing moved on a cancel, so it
  // buys nothing there — but an execute whose response was lost, or a
  // replayed one, closes down the same path, and a Portfolio still
  // showing the pre-conversion figures is the one outcome that reads as
  // money gone missing.
  const closeConvert = useCallback(
    (open: boolean) => {
      setConvertOpen(open);
      if (open || action !== "convert") return;
      void qc.invalidateQueries({ queryKey: keys.wallet });
      void navigate({ to: "/portfolio", replace: true });
    },
    [action, navigate, qc],
  );

  // Cold load: the stored mode is still in flight, so `mode` is only
  // the CASH default. Redirecting on it would bounce a GAME user off
  // their own wallet on every refresh and every pasted link. Wait.
  if (resolving) {
    return (
      <SubPage title={t("balances.title")}>
        <Skeleton className="h-40 w-full" />
      </SubPage>
    );
  }

  // `replace` so the back button returns to wherever the user actually
  // came from, not to a /wallet that would redirect them straight back.
  if (!walletPageEnabled(mode)) return <Navigate to="/portfolio" replace />;

  return (
    <SubPage title={t("balances.title")}>
      <div className="grid gap-4">
        <WalletBalancesPanel emphasize={action === "convert" ? undefined : action} />

        {/* Converting is a GREEN action, and GREEN only exists in GAME —
            which the guard above has already established is the only
            mode that renders this page. The GREEN→GOLD route reaches a
            CASH user from the redeem dialog's explicit convert step. */}
        {conversionAllowed ? (
          <>
            <Button block onClick={() => setConvertOpen(true)} data-testid="open-conversion">
              {t("convert.openCta")}
            </Button>

            <ConversionHistoryCard />
          </>
        ) : null}
      </div>

      {/* GREEN→RED is preselected for a Portfolio Convert: the user is
          in GAME and the natural move is winnings back into stake. Both
          routes stay listed, so it is a starting point and not a
          decision taken for them. */}
      {conversionAllowed ? (
        <ConversionDialog
          open={convertOpen}
          onOpenChange={closeConvert}
          initialRoute={action === "convert" ? "GREEN_TO_RED" : undefined}
        />
      ) : null}
    </SubPage>
  );
}
